import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField, Typography } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { addDays } from 'date-fns';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Dialog } from '../../../../../components/molecules/dialog';

import { useDownloadDiscountTickets } from '@/features/visitors/hooks/mutation/use-download-discount-tickets';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  from: Date;
  to: Date | null;
}

const defaultValues: FormValues = {
  from: new Date(),
  to: addDays(new Date(), 30),
};

export const DownloadDiscountedTicketsDialog: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['visits']);

  const { mutate: downloadDiscountedTickets } = useDownloadDiscountTickets(onClose);

  const validationSchema = z.object({
    from: z.date(),
    to: z.date(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    downloadDiscountedTickets({ ...data, to: new Date(data?.to || new Date()) });
  };

  return (
    <Dialog
      title={t('visits:downloadDialog.title')}
      variant="info"
      onCancel={onClose}
      onConfirm={handleSubmit(onSubmit)}
      isConfirmButtonDisabled={!isValid}
      open={isOpen}>
      <Typography sx={{ mb: 2 }}>{t('visits:downloadDialog.message')}</Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
        <Controller
          name="from"
          control={control}
          render={({ field: { name, value, onBlur, onChange }, fieldState: { error } }) => (
            <MobileDatePicker
              format={'dd-MM-yyyy'}
              value={value}
              onAccept={date => {
                onChange(date);

                setValue('to', null);
              }}
              disableFuture
              slots={{
                textField: props => (
                  <TextField
                    {...props}
                    size="medium"
                    name={name}
                    onBlur={onBlur}
                    required
                    label={t('visits:downloadDialog.from')}
                    error={!!error}
                    helperText={error?.message}
                  />
                ),
              }}
            />
          )}
        />
        <Controller
          name="to"
          control={control}
          render={({ field: { name, value, onBlur, onChange }, fieldState: { error } }) => (
            <MobileDatePicker
              format={'dd-MM-yyyy'}
              value={value}
              onAccept={onChange}
              disableFuture
              slots={{
                textField: props => (
                  <TextField
                    {...props}
                    size="medium"
                    name={name}
                    onBlur={onBlur}
                    required
                    label={t('visits:downloadDialog.to')}
                    error={!!error}
                    helperText={error?.message}
                  />
                ),
              }}
            />
          )}
        />
      </Box>
    </Dialog>
  );
};
